import React, { useEffect, useRef, useState } from 'react';

const scriptCache = {};

/** Loads a provider's external_api.js once per domain. */
const loadJitsiScript = (domain) => {
  if (scriptCache[domain]) return scriptCache[domain];

  scriptCache[domain] = new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);

    const script = document.createElement('script');
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.onload = () =>
      window.JitsiMeetExternalAPI
        ? resolve(window.JitsiMeetExternalAPI)
        : reject(new Error('Video library failed to initialise'));
    script.onerror = () => {
      delete scriptCache[domain];
      reject(new Error('Could not reach the video service'));
    };
    document.body.appendChild(script);
  });

  return scriptCache[domain];
};

/**
 * Embeds a Jitsi room. The room name is the shared secret, so it is always
 * supplied by the backend rather than derived on the client.
 */
export default function JitsiRoom({
  domain = 'meet.jit.si',
  roomName,
  displayName,
  subject,
  isHost = false,
  onJoined,
  onLeft,
  className = '',
}) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep the latest callbacks without re-creating the room on every render.
  const handlersRef = useRef({ onJoined, onLeft });
  useEffect(() => {
    handlersRef.current = { onJoined, onLeft };
  }, [onJoined, onLeft]);

  useEffect(() => {
    if (!roomName || !containerRef.current) return undefined;

    let disposed = false;

    loadJitsiScript(domain)
      .then((JitsiMeetExternalAPI) => {
        if (disposed || !containerRef.current) return;

        const api = new JitsiMeetExternalAPI(domain, {
          roomName,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: { displayName: displayName || 'Guest' },
          configOverwrite: {
            prejoinPageEnabled: true,
            disableDeepLinking: true,
            startWithAudioMuted: !isHost,
            startWithVideoMuted: false,
            enableWelcomePage: false,
            defaultLanguage: 'en',
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_BRAND_WATERMARK: false,
            MOBILE_APP_PROMO: false,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
            TOOLBAR_BUTTONS: [
              'microphone',
              'camera',
              'desktop',
              'chat',
              'raisehand',
              'tileview',
              'fullscreen',
              'settings',
              'videoquality',
              'hangup',
            ],
          },
        });

        apiRef.current = api;
        setLoading(false);

        if (subject) {
          try {
            api.executeCommand('subject', subject);
          } catch (e) {
            /* subject is cosmetic - ignore if the provider rejects it */
          }
        }

        api.addEventListener('videoConferenceJoined', () => handlersRef.current.onJoined?.());
        api.addEventListener('readyToClose', () => handlersRef.current.onLeft?.());
        api.addEventListener('videoConferenceLeft', () => handlersRef.current.onLeft?.());
      })
      .catch((err) => {
        if (disposed) return;
        setError(err.message || 'Could not start the video call');
        setLoading(false);
      });

    return () => {
      disposed = true;
      try {
        apiRef.current?.dispose();
      } catch (e) {
        /* already disposed */
      }
      apiRef.current = null;
    };
  }, [domain, roomName, displayName, subject, isHost]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-slate-900 text-center ${className}`}>
        <div className="p-8">
          <p className="text-rose-400 font-medium">{error}</p>
          <p className="mt-2 text-sm text-slate-400">
            Check your internet connection and refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden bg-slate-900 ${className}`}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-700 border-t-sky-500" />
            <p className="mt-3 text-sm text-slate-400">Connecting to the consultation room…</p>
          </div>
        </div>
      )}
      {/* Absolute (not h-full) so the iframe's height:100% always has a box to
          resolve against, even when an ancestor's height comes from a flex
          chain rather than an explicit one. */}
      <div ref={containerRef} className="jitsi-embed absolute inset-0" />
    </div>
  );
}
